import type { ReactNode } from "react";

import type { BuilderContextInterface, RegisteredComponents } from "@builder.io/sdk-react";
import { Blocks, isEditing } from "@builder.io/sdk-react";
import type { z } from "zod";

import {
  VideoContainer as VideoContainerUi,
  videoPlayerContainerPropsToVideoPlayerProps,
} from "@jti/ui/components/VideoContainer/VideoContainer";
import { VideoPlayer } from "@jti/ui/components/VideoPlayer";

import type { LoyaltyVideoPreviewProps } from "@/components/LoyaltyVideoPreview/LoyaltyVideoPreview";
import { LoyaltyVideoPreview } from "@/components/LoyaltyVideoPreview/LoyaltyVideoPreview";
import { getMergedChildBlockEntries } from "@/libs/builder/utils/getMergedChildBlockEntries";

import { usePublisherData } from "../VideoPlayer/_internal/usePublisherData";

import { loyaltyVideoPreviewSchema, videoPlayerSchema } from "./schema";
import { VideoContainerJsonLD } from "./VideoContainerJsonLD";

type VideoPlayerData = z.infer<typeof videoPlayerSchema>;

const ALLOWED_COMPONENT_NAMES = ["VideoPlayer", "LoyaltyVideoPreview"] as const;
type AllowedComponentName = (typeof ALLOWED_COMPONENT_NAMES)[number];

function isValidBlock(block: BuilderBlock): block is BuilderBlock & {
  component: { name: AllowedComponentName; options: Record<string, unknown> };
} {
  return (
    typeof block.component?.options === "object" &&
    ALLOWED_COMPONENT_NAMES.includes(block.component?.name as AllowedComponentName)
  );
}

type VideoContainerProps = {
  title: string;
  childBlocks: BuilderBlock[];
  builderBlock: BuilderBlock;
  builderContext: BuilderContextInterface;
  builderComponents: RegisteredComponents;
};

function renderVideoPlayerBlock(options: Record<string, unknown>): ReactNode | null {
  const { data, success, error } = videoPlayerSchema.safeParse(options);

  if (!success) {
    console.error("VideoPlayer data validation failed", error);
    return null;
  }

  return <VideoPlayer {...videoPlayerContainerPropsToVideoPlayerProps(data)} />;
}

function renderLoyaltyVideoPreviewBlock(options: Record<string, unknown>): ReactNode | null {
  const { data, success, error } = loyaltyVideoPreviewSchema.safeParse(options);

  if (!success) {
    console.error("LoyaltyVideoPreview data validation failed", error);
    return null;
  }

  return <LoyaltyVideoPreview {...(data as unknown as LoyaltyVideoPreviewProps)} />;
}

function parseVideoPlayerItems(childBlocksArray: BuilderBlock[]): VideoPlayerData[] {
  return childBlocksArray
    .filter(isValidBlock)
    .filter((block) => block.component.name === "VideoPlayer")
    .map((block) => {
      const { data, success, error } = videoPlayerSchema.safeParse(block.component.options);

      if (!success) {
        console.error("VideoPlayer JSON-LD data validation failed", error);
        return null;
      }

      return data;
    })
    .filter((data): data is VideoPlayerData => data !== null);
}

export function VideoContainer({
  title,
  childBlocks,
  builderBlock,
  builderContext,
  builderComponents,
}: Readonly<VideoContainerProps>) {
  const publisher = usePublisherData();
  const childBlockEntries = getMergedChildBlockEntries(builderBlock, childBlocks);
  const childBlocksArray = childBlockEntries.map(({ block }) => block);

  if (isEditing()) {
    return (
      <VideoContainerUi
        title={title}
        items={childBlockEntries
          .map(({ block, path }) => (
            <Blocks
              key={block.id ?? path}
              parent={builderBlock?.id}
              path={path}
              blocks={[block]}
              context={builderContext}
              registeredComponents={builderComponents}
            />
          ))
          .concat(
            <Blocks
              key="add-video-block"
              parent={builderBlock?.id}
              path="childBlocks"
              blocks={[]}
              context={builderContext}
              registeredComponents={builderComponents}
            />
          )}
      />
    );
  }

  const jsonLdItems = parseVideoPlayerItems(childBlocksArray);

  const items = childBlocksArray
    .filter(isValidBlock)
    .map((block): ReactNode | null => {
      const { name, options } = block.component;

      if (name === "LoyaltyVideoPreview") {
        return renderLoyaltyVideoPreviewBlock(options);
      }

      return renderVideoPlayerBlock(options);
    })
    .filter((item): item is ReactNode => item !== null);

  return (
    <>
      <VideoContainerJsonLD title={title} items={jsonLdItems} publisher={publisher} />
      <VideoContainerUi title={title} items={items} />
    </>
  );
}
